import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
  useCallback,
} from 'react'
import * as ToastPrimitive from '@radix-ui/react-toast'
import { Toast, ToastProps } from './toast'

export type ToastOptions = Omit<ToastProps, 'open' | 'onOpenChange'> & {
  duration?: number
}

type ToastContextType = {
  show: (options: ToastOptions) => void
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<
    (ToastOptions & {
      id: string
    })[]
  >([])

  const show = useCallback((options: ToastOptions) => {
    const newToast = { ...options, id: crypto.randomUUID() }
    setToasts((prev) => [...prev, newToast])
  }, [])

  return (
    <ToastContext.Provider value={useMemo(() => ({ show }), [show])}>
      <ToastPrimitive.Provider>
        {children}
        {toasts.map((toast) => (
          <ToastPrimitive.Root
            key={toast.id}
            open
            onOpenChange={() =>
              setToasts((prev) => prev.filter((t) => t.id !== toast.id))
            }
            duration={toast.duration || 3000}
          >
            <Toast {...toast} />
          </ToastPrimitive.Root>
        ))}
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const useToast = () => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}
